import { z } from "zod";
import {
  UuidField,
  ParticipantIdField,
  TimestampField,
} from "./common";

/**
 * WebSocket channel subscription schemas
 * Defines the channel patterns and required parameters for real-time event subscriptions
 */

// Channel types that can be subscribed to
const ChannelTypeSchema = z.enum([
  "composing",    // Composition events (per-participant typing with content)
  "typing",       // Typing events (thread-level typing indicators)
  "thread",       // Thread messages
  "outreach",     // Outreach messages  
  "context",      // Context events
  "tags",         // Tag events (future)
  "party",        // Party events (future)
]);

export type ChannelType = z.infer<typeof ChannelTypeSchema>;

// Channel pattern definitions with required parameters
export interface ChannelPattern {
  type: ChannelType;
  pattern: string;
  requiredParams: Array<'workspaceId' | 'conversationId' | 'threadId' | 'participantId'>;
  description: string;
}

export const CHANNEL_PATTERNS: Record<ChannelType, ChannelPattern> = {
  composing: {
    type: 'composing',
    pattern: 'composing.{workspaceId}.{conversationId}.{threadId}.{participantId}',
    requiredParams: ['workspaceId', 'conversationId', 'threadId', 'participantId'],
    description: 'Per-participant composition with content preview',
  },
  typing: {
    type: 'typing',
    pattern: 'typing.{workspaceId}.{conversationId}.{threadId}',
    requiredParams: ['workspaceId', 'conversationId', 'threadId'],
    description: 'Thread-level typing indicators (all participants)',
  },
  thread: {
    type: 'thread',
    pattern: 'thread.{workspaceId}.{conversationId}.{threadId}',
    requiredParams: ['workspaceId', 'conversationId', 'threadId'],
    description: 'Thread message updates',
  },
  outreach: {
    type: 'outreach',
    pattern: 'outreach.{workspaceId}.{conversationId}.{threadId}.{participantId}',
    requiredParams: ['workspaceId', 'conversationId', 'threadId', 'participantId'],
    description: 'Outreach messages for specific participant',
  },
  context: {
    type: 'context',
    pattern: 'context.{workspaceId}.{conversationId}.{threadId}.{participantId}',
    requiredParams: ['workspaceId', 'conversationId', 'threadId', 'participantId'],
    description: 'Context events for specific participant',
  },
  tags: {
    type: 'tags',
    pattern: 'tags.{workspaceId}',
    requiredParams: ['workspaceId'],
    description: 'Tag creation/update/delete events',
  },
  party: {
    type: 'party',
    pattern: 'party.{workspaceId}',
    requiredParams: ['workspaceId'],
    description: 'Participant and group events',
  },
};

// Subscription parameters
const SubscriptionParamsSchema = z.object({
  workspaceId: UuidField.optional(),
  conversationId: UuidField.optional(),
  threadId: UuidField.optional(),
  participantId: ParticipantIdField.optional(),
});

export type SubscriptionParams = z.infer<typeof SubscriptionParamsSchema>;

// Active subscription
export const Subscription = z.object({
  channelType: ChannelTypeSchema,
  channelName: z.string(),
  params: SubscriptionParamsSchema,
  subscribedAt: TimestampField,
});

// type Subscription = z.infer<typeof Subscription>;

// Subscription request (from client to server)
export const SubscriptionRequest = z.object({
  type: z.literal('subscribe'),
  channel: z.string(),
});

// type SubscriptionRequest = z.infer<typeof SubscriptionRequest>;

// Build channel name from pattern and params
export function buildChannelName(
  channelType: ChannelType,
  params: SubscriptionParams
): string | null {
  const pattern = CHANNEL_PATTERNS[channelType];
  
  // Check if all required params are provided
  for (const param of pattern.requiredParams) {
    if (!params[param]) {
      return null;
    }
  }
  
  // Replace placeholders with actual values
  let channelName = pattern.pattern;
  for (const [key, value] of Object.entries(params)) {
    if (value) {
      channelName = channelName.replace(`{${key}}`, value);
    }
  }
  
  return channelName;
}

export const subscriptionSchemas = {
  ChannelType: ChannelTypeSchema,
  SubscriptionParams: SubscriptionParamsSchema,
  Subscription,
  SubscriptionRequest,
};

